import {Component, computed, effect, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatDivider} from "@angular/material/divider"
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu"
import {RulerComponent} from "@common/components/ruler/ruler.component"
import {Vector2Like} from "@cm/math"
import {assertNever} from "@cm/utils"
import {MeasurementUnit} from "@common/components/canvas"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-ruler-workspace",
    imports: [MatDivider, MatMenu, MatMenuItem, RulerComponent, MatMenuTrigger],
    templateUrl: "./ruler-workspace.component.html",
    styleUrl: "./ruler-workspace.component.scss",
})
export class RulerWorkspaceComponent {
    readonly $inputShowCursorLabel = input<boolean>(false, {alias: "showCursorLabel"})
    readonly pixelsPerCm = input<number>(10)
    readonly workspaceZoom = input<number>(1)
    readonly workspaceOffsetPx = input<Vector2Like>({x: 0, y: 0})
    readonly cursorPositionPx = input<Vector2Like | undefined>(undefined)

    readonly $showCursorLabel = signal<boolean>(false)
    readonly $measurementUnit = signal<MeasurementUnit>("cm")

    protected readonly pixelsPerUnit = computed(() => {
        const unit = this.$measurementUnit()
        switch (unit) {
            case "px":
                return 1
            case "mm":
                return this.pixelsPerCm() / 10
            case "cm":
                return this.pixelsPerCm()
            case "in":
                return this.pixelsPerCm() * 2.54
            default:
                assertNever(unit)
        }
    })

    protected readonly zoomedPixelsPerUnit = computed(() => {
        return this.workspaceZoom() * this.pixelsPerUnit()
    })

    protected readonly cursorPositionInUnits = computed(() => {
        const cursorPosPx = this.cursorPositionPx()
        if (!cursorPosPx) {
            return undefined
        }
        const pixelsPerUnit = this.pixelsPerUnit()
        return {
            x: cursorPosPx.x / pixelsPerUnit,
            y: cursorPosPx.y / pixelsPerUnit,
        }
    })

    protected readonly workspaceOffsetInUnits = computed(() => {
        const offsetPx = this.workspaceOffsetPx()
        const pixelsPerUnit = this.zoomedPixelsPerUnit()
        return {
            x: offsetPx.x / pixelsPerUnit,
            y: offsetPx.y / pixelsPerUnit,
        }
    })

    constructor() {
        effect(() => {
            this.$showCursorLabel.set(this.$inputShowCursorLabel())
        })
    }

    readonly $measurementUnitOptions = computed((): MeasurementUnit[] => ["px", "mm", "cm", "in"])
}
